import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
    Phone, PhoneCall, PhoneOff, MessageSquare, Send, Clock, User,
    Plus, Calendar, CheckCircle, XCircle, RefreshCw, Trash2, Edit3,
    PhoneIncoming, PhoneOutgoing, ArrowRight
} from 'lucide-react';

// ─── PHONE DIALER TAB ────────────────────────────────────────
export function PhoneDialerTab() {
    const [callLogs, setCallLogs] = useState([]);
    const [smsLogs, setSmsLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialNumber, setDialNumber] = useState('');
    const [dialName, setDialName] = useState('');
    const [calling, setCalling] = useState(false);
    const [smsTo, setSmsTo] = useState('');
    const [smsMsg, setSmsMsg] = useState('');
    const [smsName, setSmsName] = useState('');
    const [sendingSms, setSendingSms] = useState(false);
    const [activeView, setActiveView] = useState('dialer');
    const [editNotes, setEditNotes] = useState(null);
    const [notesText, setNotesText] = useState('');
    const [config, setConfig] = useState(null);

    const loadData = useCallback(async () => {
        try {
            const [calls, sms, cfg] = await Promise.all([
                API.get('/signalwire/calls'),
                API.get('/signalwire/sms-logs'),
                API.get('/signalwire/config'),
            ]);
            setCallLogs(calls.data);
            setSmsLogs(sms.data);
            setConfig(cfg.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const makeCall = async () => {
        if (!dialNumber) return;
        setCalling(true);
        try {
            await API.post('/signalwire/call', {
                to: dialNumber,
                contact_name: dialName || 'Unknown',
            });
            setDialNumber('');
            setDialName('');
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Call failed');
        }
        finally { setCalling(false); }
    };

    const sendSms = async () => {
        if (!smsTo || !smsMsg) return;
        setSendingSms(true);
        try {
            await API.post('/signalwire/sms', {
                to: smsTo,
                message: smsMsg,
                contact_name: smsName || '',
            });
            setSmsTo('');
            setSmsMsg('');
            setSmsName('');
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'SMS failed');
        }
        finally { setSendingSms(false); }
    };

    const saveNotes = async (callId) => {
        try {
            await API.post(`/signalwire/call/${callId}/notes`, { notes: notesText });
            setEditNotes(null);
            await loadData();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;

    return (
        <div className="space-y-6" data-testid="phone-dialer-tab">
            {/* Config Status */}
            {config && (
                <div className="flex items-center gap-3 text-xs text-zinc-500 bg-zinc-50 rounded-lg px-4 py-2">
                    <Phone className="w-3.5 h-3.5" />
                    <span>SignalWire: <strong>{config.phone_number}</strong></span>
                    <Badge className={config.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {config.configured ? 'Connected' : 'Not Configured'}
                    </Badge>
                </div>
            )}

            {/* Tabs: Dialer / SMS / History */}
            <div className="flex bg-zinc-100 rounded-xl p-1 w-fit">
                {[['dialer', 'Dialer', PhoneCall], ['sms', 'SMS', MessageSquare], ['history', 'Call Log', Clock]].map(([key, label, Icon]) => (
                    <button key={key} onClick={() => setActiveView(key)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${activeView === key ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
                        data-testid={`dialer-tab-${key}`}>
                        <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                ))}
            </div>

            {activeView === 'dialer' && (
                <div className="bg-white rounded-2xl border border-zinc-200 p-6 max-w-md">
                    <h3 className="text-lg font-semibold text-zinc-900 font-['Outfit'] mb-4 flex items-center gap-2">
                        <PhoneCall className="w-5 h-5 text-emerald-600" /> Click-to-Call
                    </h3>
                    <div className="space-y-3">
                        <Input placeholder="Contact Name (optional)" value={dialName}
                            onChange={e => setDialName(e.target.value)}
                            className="h-10 text-sm" data-testid="dial-name" />
                        <Input placeholder="Phone Number (e.g. 555-123-4567)" value={dialNumber}
                            onChange={e => setDialNumber(e.target.value)}
                            className="h-12 text-lg font-mono" data-testid="dial-number" />
                        <Button onClick={makeCall} disabled={calling || !dialNumber}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base font-semibold rounded-xl"
                            data-testid="dial-call-btn">
                            {calling ? <RefreshCw className="w-5 h-5 animate-spin mr-2" /> : <Phone className="w-5 h-5 mr-2" />}
                            {calling ? 'Connecting...' : 'Call Now'}
                        </Button>
                    </div>
                </div>
            )}

            {activeView === 'sms' && (
                <div className="bg-white rounded-2xl border border-zinc-200 p-6 max-w-lg">
                    <h3 className="text-lg font-semibold text-zinc-900 font-['Outfit'] mb-4 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-blue-600" /> Send SMS
                    </h3>
                    <div className="space-y-3">
                        <Input placeholder="Contact Name (optional)" value={smsName}
                            onChange={e => setSmsName(e.target.value)} className="h-9 text-sm" data-testid="sms-name" />
                        <Input placeholder="Phone Number" value={smsTo}
                            onChange={e => setSmsTo(e.target.value)} className="h-10 font-mono" data-testid="sms-to" />
                        <Textarea placeholder="Type your message..." value={smsMsg}
                            onChange={e => setSmsMsg(e.target.value)} rows={3} className="text-sm" data-testid="sms-message" />
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-400">{smsMsg.length}/160 chars</span>
                            <Button onClick={sendSms} disabled={sendingSms || !smsTo || !smsMsg}
                                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sms-send-btn">
                                {sendingSms ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                Send SMS
                            </Button>
                        </div>
                    </div>

                    {/* SMS History */}
                    {smsLogs.length > 0 && (
                        <div className="mt-6 border-t pt-4">
                            <h4 className="text-sm font-medium text-zinc-700 mb-3">Recent SMS</h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {smsLogs.slice(0, 10).map(sms => (
                                    <div key={sms.id} className="bg-zinc-50 rounded-lg p-3 text-xs">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium">{sms.contact_name || sms.to_number}</span>
                                            <Badge className={sms.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{sms.status}</Badge>
                                        </div>
                                        <p className="text-zinc-600 line-clamp-2">{sms.message}</p>
                                        <div className="text-zinc-400 mt-1">{new Date(sms.created_at).toLocaleString()}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeView === 'history' && (
                <div className="space-y-3">
                    <h3 className="text-sm font-medium text-zinc-700 font-['Outfit']">Call History ({callLogs.length})</h3>
                    {callLogs.length === 0 ? (
                        <div className="bg-zinc-50 rounded-xl p-8 text-center text-sm text-zinc-500">
                            <PhoneOff className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                            No calls yet. Use the dialer to make your first call.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {callLogs.map(call => (
                                <div key={call.id} className="bg-white rounded-xl border border-zinc-200 p-4" data-testid={`call-log-${call.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {call.direction === 'inbound'
                                                ? <PhoneIncoming className="w-4 h-4 text-blue-500" />
                                                : <PhoneOutgoing className="w-4 h-4 text-emerald-500" />}
                                            <div>
                                                <div className="text-sm font-medium">{call.contact_name || call.to_number || call.from_number}</div>
                                                <div className="text-xs text-zinc-500">{call.direction === 'inbound' ? call.from_number : call.to_number}</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <Badge className={
                                                call.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                                call.status === 'initiated' ? 'bg-blue-100 text-blue-700' :
                                                'bg-zinc-100 text-zinc-600'
                                            }>{call.status}</Badge>
                                            <div className="text-[10px] text-zinc-400 mt-1">{new Date(call.created_at).toLocaleString()}</div>
                                        </div>
                                    </div>
                                    {call.duration > 0 && <div className="text-xs text-zinc-500 mt-1">Duration: {call.duration}s</div>}
                                    {call.notes && <div className="text-xs text-zinc-600 mt-2 bg-zinc-50 rounded p-2">{call.notes}</div>}

                                    {editNotes === call.id ? (
                                        <div className="mt-2 flex gap-2">
                                            <Textarea value={notesText} onChange={e => setNotesText(e.target.value)}
                                                placeholder="Add call notes..." className="text-xs min-h-[60px] flex-1" />
                                            <div className="flex flex-col gap-1">
                                                <Button size="sm" onClick={() => saveNotes(call.id)} className="bg-zinc-900 text-white text-xs h-6">Save</Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEditNotes(null)} className="text-xs h-6">Cancel</Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => { setEditNotes(call.id); setNotesText(call.notes || ''); }}
                                            className="text-xs text-zinc-400 hover:text-zinc-600 mt-2 flex items-center gap-1">
                                            <Edit3 className="w-3 h-3" /> {call.notes ? 'Edit notes' : 'Add notes'}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── APPOINTMENTS TAB ────────────────────────────────────────
export function AppointmentsTab() {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ contact_name: '', phone: '', date: '', time: '', type: 'phone_call', notes: '' });
    const [editId, setEditId] = useState(null);

    const loadAppointments = useCallback(async () => {
        try {
            const { data } = await API.get('/signalwire/appointments');
            setAppointments(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadAppointments(); }, [loadAppointments]);

    const createAppointment = async () => {
        try {
            if (editId) {
                await API.put(`/signalwire/appointments/${editId}`, {
                    contact_name: form.contact_name,
                    appointment_date: form.date,
                    appointment_time: form.time,
                    type: form.type,
                    notes: form.notes,
                });
            } else {
                await API.post('/signalwire/appointments', form);
            }
            setShowForm(false);
            setEditId(null);
            setForm({ contact_name: '', phone: '', date: '', time: '', type: 'phone_call', notes: '' });
            await loadAppointments();
        } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    };

    const deleteAppointment = async (id) => {
        if (!window.confirm('Delete this appointment?')) return;
        try {
            await API.delete(`/signalwire/appointments/${id}`);
            await loadAppointments();
        } catch (err) { console.error(err); }
    };

    const updateStatus = async (id, status) => {
        try {
            await API.put(`/signalwire/appointments/${id}`, { status });
            await loadAppointments();
        } catch (err) { console.error(err); }
    };

    const startEdit = (appt) => {
        setEditId(appt.id);
        setForm({
            contact_name: appt.contact_name || '',
            phone: appt.caller_phone || '',
            date: appt.appointment_date || '',
            time: appt.appointment_time || '',
            type: appt.type || 'phone_call',
            notes: appt.notes || '',
        });
        setShowForm(true);
    };

    if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;

    const statusColors = {
        pending: 'bg-amber-100 text-amber-700',
        confirmed: 'bg-emerald-100 text-emerald-700',
        completed: 'bg-blue-100 text-blue-700',
        cancelled: 'bg-red-100 text-red-700',
    };

    const today = new Date().toISOString().split('T')[0];
    const upcoming = appointments.filter(a => a.appointment_date >= today && a.status !== 'cancelled');
    const past = appointments.filter(a => a.appointment_date < today || a.status === 'cancelled');

    return (
        <div className="space-y-6" data-testid="appointments-tab">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-zinc-900 font-['Outfit'] flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-amber-600" /> Appointments
                </h3>
                <Button onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ contact_name: '', phone: '', date: '', time: '', type: 'phone_call', notes: '' }); }}
                    className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs" data-testid="new-appointment-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> New Appointment
                </Button>
            </div>

            {showForm && (
                <div className="bg-white rounded-xl border border-zinc-200 p-5 max-w-lg">
                    <h4 className="text-sm font-medium text-zinc-700 mb-3">{editId ? 'Edit' : 'New'} Appointment</h4>
                    <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="Contact Name" value={form.contact_name}
                            onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                            className="h-9 text-sm" data-testid="appt-name" />
                        <Input placeholder="Phone" value={form.phone}
                            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                            className="h-9 text-sm" data-testid="appt-phone" />
                        <Input type="date" value={form.date}
                            onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                            className="h-9 text-sm" data-testid="appt-date" />
                        <Input placeholder="Time (e.g. 2:00 PM)" value={form.time}
                            onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                            className="h-9 text-sm" data-testid="appt-time" />
                        <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                            className="h-9 text-sm border border-zinc-200 rounded-md px-3" data-testid="appt-type">
                            <option value="phone_call">Phone Call</option>
                            <option value="in_person">In Person</option>
                            <option value="video">Video Call</option>
                            <option value="property_showing">Property Showing</option>
                        </select>
                    </div>
                    <Textarea placeholder="Notes..." value={form.notes}
                        onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        className="text-sm mt-3" rows={2} data-testid="appt-notes" />
                    <div className="flex gap-2 mt-3">
                        <Button onClick={createAppointment} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="save-appt-btn">
                            {editId ? 'Update' : 'Create'} Appointment
                        </Button>
                        <Button variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }} className="text-xs">Cancel</Button>
                    </div>
                </div>
            )}

            {/* Upcoming */}
            <div>
                <h4 className="text-sm font-medium text-zinc-500 mb-2">Upcoming ({upcoming.length})</h4>
                {upcoming.length === 0 ? (
                    <div className="bg-zinc-50 rounded-xl p-6 text-center text-sm text-zinc-500">
                        <Calendar className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                        No upcoming appointments
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {upcoming.map(appt => (
                            <div key={appt.id} className="bg-white rounded-xl border border-zinc-200 p-4" data-testid={`appt-${appt.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-zinc-400" />
                                        <span className="text-sm font-medium">{appt.contact_name || appt.caller_phone || 'Unknown'}</span>
                                    </div>
                                    <Badge className={statusColors[appt.status] || 'bg-zinc-100 text-zinc-600'}>{appt.status}</Badge>
                                </div>
                                <div className="text-xs text-zinc-500 space-y-1">
                                    <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {appt.appointment_date} at {appt.appointment_time}</div>
                                    <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {appt.caller_phone || 'N/A'}</div>
                                    <div className="capitalize">{appt.type?.replace('_', ' ')}</div>
                                    {appt.source === 'inbound_call' && <Badge className="bg-blue-50 text-blue-600 text-[10px]">From incoming call</Badge>}
                                </div>
                                {appt.notes && <p className="text-xs text-zinc-600 mt-2 bg-zinc-50 rounded p-2">{appt.notes}</p>}
                                {appt.transcription && <p className="text-xs text-blue-600 mt-1 italic">"{appt.transcription}"</p>}
                                <div className="flex items-center gap-1.5 mt-3">
                                    {appt.status === 'pending' && (
                                        <Button size="sm" onClick={() => updateStatus(appt.id, 'confirmed')}
                                            className="bg-emerald-600 text-white text-[10px] h-6 px-2">
                                            <CheckCircle className="w-3 h-3 mr-0.5" /> Confirm
                                        </Button>
                                    )}
                                    {appt.status === 'confirmed' && (
                                        <Button size="sm" onClick={() => updateStatus(appt.id, 'completed')}
                                            className="bg-blue-600 text-white text-[10px] h-6 px-2">
                                            <CheckCircle className="w-3 h-3 mr-0.5" /> Complete
                                        </Button>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => startEdit(appt)} className="text-[10px] h-6 px-2">
                                        <Edit3 className="w-3 h-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => updateStatus(appt.id, 'cancelled')} className="text-[10px] h-6 px-2 text-red-500">
                                        <XCircle className="w-3 h-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => deleteAppointment(appt.id)} className="text-[10px] h-6 px-2 text-red-400">
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Past */}
            {past.length > 0 && (
                <div>
                    <h4 className="text-sm font-medium text-zinc-400 mb-2">Past / Cancelled ({past.length})</h4>
                    <div className="space-y-2">
                        {past.slice(0, 5).map(appt => (
                            <div key={appt.id} className="bg-zinc-50 rounded-lg p-3 flex items-center justify-between text-xs text-zinc-500">
                                <div className="flex items-center gap-2">
                                    <User className="w-3 h-3" />
                                    <span>{appt.contact_name || appt.caller_phone}</span>
                                    <span>{appt.appointment_date}</span>
                                </div>
                                <Badge className={statusColors[appt.status] || 'bg-zinc-100'}>{appt.status}</Badge>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
